import React, { useEffect, useRef, useState } from 'react';
import { useSalon } from '../context/SalonContext';
import { LOCATIONS, LOCATION_LIST } from '../data/locations';

const buttonStyle = {
  position: 'fixed',
  right: '20px',
  width: '60px',
  height: '60px',
  borderRadius: '50%',
  backgroundColor: '#28a745',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textDecoration: 'none',
  border: 'none',
  padding: 0,
  boxShadow: '0 2px 5px rgba(0,0,0,0.3)',
  zIndex: 3000,
  transition: 'transform 0.2s ease-in-out',
  cursor: 'pointer',
  touchAction: 'manipulation',
};

const phoneIcon = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24" height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="white"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ pointerEvents: 'none' }}
  >
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2
      19.86 19.86 0 0 1-8.63-3.06
      19.29 19.29 0 0 1-6-6
      19.86 19.86 0 0 1-3.05-8.62
      2 2 0 0 1 2-2.19h3a2 2 0 0 1 2
      1.72c.13.86.35 1.7.64 2.5a2
      2 0 0 1-.45 2.11L7.91 9.91a16
      16 0 0 0 6 6l1.39-1.39a2 2
      0 0 1 2.11-.45c.8.29 1.64.51
      2.5.64a2 2 0 0 1 1.72 2z">
    </path>
  </svg>
);

// iOS: над home-индикатора
const getBottomOffset = () => {
  if (typeof window === 'undefined' || !window.CSS || !window.CSS.supports) return '20px';
  if (window.CSS.supports('bottom: env(safe-area-inset-bottom)')) {
    return 'calc(env(safe-area-inset-bottom, 0px) + 20px)';
  }
  if (window.CSS.supports('bottom: constant(safe-area-inset-bottom)')) {
    return 'calc(constant(safe-area-inset-bottom, 0px) + 20px)';
  }
  return '20px';
};

// Плаващ бутон за обаждане. При избран салон (в резервациите) звъни директно
// на него; иначе отваря меню за избор на салон.
function FloatingCallButton() {
  const { selectedLocationId } = useSalon();
  const [hover, setHover] = useState(false);
  const [open, setOpen] = useState(false);
  const [bottomOffset, setBottomOffset] = useState('20px');
  const wrapperRef = useRef(null);
  const supportsHover =
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(hover: hover)').matches
      : false;

  useEffect(() => {
    const update = () => setBottomOffset(getBottomOffset());
    update();
    window.addEventListener('orientationchange', update);
    return () => window.removeEventListener('orientationchange', update);
  }, []);

  // Менюто се затваря при клик извън него, Escape или избор на салон.
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    setOpen(false);
  }, [selectedLocationId]);

  // Опциите са чисти <a href="tel:"> без никакъв JavaScript при докосване —
  // точно като директния бутон. Менюто се затваря при клик навън, Escape
  // или когато Safari отиде на заден план (телефонното приложение).
  useEffect(() => {
    if (!open) return undefined;
    const onHidden = () => {
      if (document.visibilityState === 'hidden') setOpen(false);
    };
    document.addEventListener('visibilitychange', onHidden);
    window.addEventListener('pagehide', onHidden);
    return () => {
      document.removeEventListener('visibilitychange', onHidden);
      window.removeEventListener('pagehide', onHidden);
    };
  }, [open]);

  const style = {
    ...buttonStyle,
    ...(supportsHover && hover ? { transform: 'scale(1.1)' } : {}),
    bottom: bottomOffset,
  };
  const hoverHandlers = supportsHover
    ? { onMouseEnter: () => setHover(true), onMouseLeave: () => setHover(false) }
    : {};

  const selected = selectedLocationId ? LOCATIONS[selectedLocationId] : null;

  if (selected) {
    return (
      <a
        href={selected.phoneLink}
        style={style}
        aria-label={`Позвънете на ${selected.name} (${selected.phoneDisplay})`}
        title={`${selected.shortName}: ${selected.phoneDisplay}`}
        {...hoverHandlers}
      >
        {phoneIcon}
      </a>
    );
  }

  return (
    <div ref={wrapperRef}>
      {open && (
        <div
          role="menu"
          aria-label="Избор на салон за обаждане"
          className="fixed right-5 z-[3000] bg-neutral-100 border border-red-800 rounded-xl shadow-xl overflow-hidden text-red-800"
          style={{ bottom: `calc(${bottomOffset} + 72px)`, minWidth: '15rem' }}
        >
          <p className="px-4 pt-3 pb-1 text-sm uppercase tracking-widest text-neutral-600">На кой салон да позвъним?</p>
          {LOCATION_LIST.map((location) => (
            <a
              key={location.id}
              role="menuitem"
              href={location.phoneLink}
              className="block px-4 py-3 hover:bg-red-800 hover:text-neutral-100 transition duration-200 border-t border-neutral-300"
            >
              <span className="block font-bold text-lg">
                {location.shortName}
                {location.isNew && <span className="ml-2 text-xs uppercase tracking-wider">ново</span>}
              </span>
              <span className="block text-base">{location.phoneDisplay}</span>
            </a>
          ))}
        </div>
      )}
      <button
        type="button"
        style={style}
        aria-label="Позвънете на Alexiev's Barbershop — изберете салон"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        {...hoverHandlers}
      >
        {phoneIcon}
      </button>
    </div>
  );
}

export default FloatingCallButton;
